import { ChakraFormBaseInput, ContentContainer } from 'app/components/Core';
import { useAppDispatch } from 'app/store';
import { updateFolderEffect } from 'app/store/SongList/effects';
import { useEffect, useState } from 'react';
import { FolderItem } from 'types/song';
import { Center, Flex, Text, VStack } from '@chakra-ui/react';
import { ReactComponent as FolderSolidIcon } from 'resources/images/folder-solid.svg';
import { ContentContainerType } from 'types/enums';
import { useSongFolderRenameForm } from './useSongFolderRenameForm';

interface SongFolderHeaderProps {
  folder: FolderItem;
}

export const SongFolderHeader = ({ folder }: SongFolderHeaderProps) => {
  const dispatch = useAppDispatch();

  const {
    register,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useSongFolderRenameForm({ folder });

  useEffect(() => {
    reset({
      title: folder.title,
    });
  }, [folder, reset]);

  const [lastValidTitle, setLastValidTitle] = useState(folder.title);

  const onSubmit = () => {
    if (folder && lastValidTitle) {
      dispatch(
        updateFolderEffect({
          guid: folder.guid,
          title: lastValidTitle,
        })
      );
    }
  };

  const songCountLabel =
    folder?.songCount === 1
      ? `${folder?.songCount} Song`
      : `${folder?.songCount} Songs`;

  return (
    <Flex height="176px" align="center" bg="M.30">
      <ContentContainer type={ContentContainerType.LIST} sx={{ py: 6, px: 4 }}>
        {folder && (
          <Flex gap={4}>
            <Center
              bg="surface2"
              rounded={4}
              h={128}
              w={128}
              color="P.40"
              flexShrink={0}
            >
              <FolderSolidIcon width={80} height={80} />
            </Center>
            <VStack justifyContent="center" alignItems="start" spacing={0}>
              <Text fontSize="xxs" fontWeight={500} opacity={0.72}>
                Folder
              </Text>
              <ChakraFormBaseInput
                placeholder="Folder Title"
                {...register('title')}
                isInvalid={Boolean(errors?.title)}
                errorMessage={errors?.title?.message}
                data-testid="song-folder-title"
                title={folder.title}
                formControlProps={{
                  pr: 4,
                }}
                formErrorMessageProps={{
                  px: 0,
                  bottom: '-40px !important',
                }}
                onChange={(e) => {
                  const value = e.target.value.trimStart();
                  setValue('title', value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  if (value) {
                    setLastValidTitle(value);
                  }
                }}
                onBlur={(e) => {
                  if (!e.target.value) {
                    setValue('title', lastValidTitle, {
                      shouldValidate: true,
                      shouldDirty: false,
                      shouldTouch: true,
                    });
                  }
                  if (isValid) {
                    onSubmit();
                  }
                }}
                fontSize="32px"
                fontWeight={500}
                lineHeight="normal"
              />
              <Text fontSize="xs">{songCountLabel}</Text>
            </VStack>
          </Flex>
        )}
      </ContentContainer>
    </Flex>
  );
};
